<template>
  <div class="cost-confirmation-summary">
    <basic-container>
      <!-- 搜索栏 -->
      <CostSearch 
        :searchForm="searchForm"
        :partnerOptions="partnerOptions"
        :channelOptions="channelOptions"
        @search="handleSearch"
        @export="handleExport"
      />

      <!-- 列表 -->
      <div class="table-container">
        <CostTable
          :tableData="tableData"
          :loading="loading"
          :summaryData="summaryData"
        />

        <!-- 分页 -->
        <div class="pagination-container">
          <ElementPagination
            :current-page="page.current"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="page.size"
            :total="page.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </basic-container>
  </div>
</template>

<script>
import CostSearch from './components/search.vue'
import CostTable from './components/table.vue'
import ElementPagination from '@/components/ElementPagination'
import { getCostConfirmationList, exportCostConfirmationList } from '@/api/financialManage/costConfirmationSummary'
import { getPartnerOptions } from '@/api/financialManage/settlementReport'
import moment from 'moment'

export default {
  name: 'CostConfirmationSummary',
  components: {
    CostSearch,
    CostTable,
    ElementPagination
  },
  data() {
    return {
      // 搜索表单
      searchForm: {
        monthRange: [],
        partners: [],
        channels: [],
        aggregationType: ['partner_id'] // 默认按月份和合作方聚合
      },
      // 合作方选项
      partnerOptions: [],
      // 渠道选项
      channelOptions: [],
      // 表格数据
      tableData: [],
      // 汇总数据
      summaryData: {},
      // 加载状态
      loading: false,
      // 分页信息
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.searchForm.monthRange = [moment().subtract(1, 'month').format('YYYY-MM'), moment().subtract(1, 'month').format('YYYY-MM')]
    this.getPartnerOptions()
    this.getTableData()
  },
  methods: {
    // 获取合作方选项
    async getPartnerOptions() {
      try {
        const { data } = await getPartnerOptions()

        console.log('获取合作方选项数据:', data);
        
        if (data.code === 200) {
          this.partnerOptions = data.data.map(item => {
            return {
              id: item.partnerId,
              partnerName: item.partnerName,
              channels: item.channelList
            }
          });
        }
      } catch (error) {
        console.error('获取合作方列表失败:', error)
        this.$message.error('获取合作方列表失败')
      }
    },

    // 获取表格数据
    async getTableData() {
      this.loading = true
      console.log('getTableData', this.searchForm, this.page);
      try {
        const params = {
          aggregations: this.searchForm.aggregationType.join(','),
          partnerIds: this.searchForm.partners.join(','),
          appids: this.searchForm.channels.join(','),
          startMonth: this.searchForm.monthRange[0],
          endMonth: this.searchForm.monthRange[1],
          current: this.page.current,
          size: this.page.size
        }

        console.log('查询参数', params);
        
        const { data } = await getCostConfirmationList(params)

        if (data.code === 200) {
          this.tableData = data.data.records.map(item => {
            return {
              ...item,
              month: item.monthStr,
              partnerName: item.partnerName,
              channelName:  item.appid ? `(${item.appid}) ${item.channelName}` : '',
              initialUnconfirmedCost: item.beginningPendingCost,
              newCostInPeriod: item.periodNewCost,
              confirmedCostAmortization: item.periodConfirmedCost,
              finalUnconfirmedCost: item.endingPendingCost
            }
          }) || []
          this.page.total = data.data.total || 0

          const summary = data.data.summary || {}
          this.summaryData = {
            ...summary,
            month: summary.monthStr,
            partnerName: summary.partnerName,
            channelName: summary.channelName,
            initialUnconfirmedCost: summary.beginningPendingCost,
            newCostInPeriod: summary.periodNewCost,
            confirmedCostAmortization: summary.periodConfirmedCost,
            finalUnconfirmedCost: summary.endingPendingCost
          }
        }
      } catch (error) {
        console.error('获取数据失败:', error)
        this.$message.error('获取数据失败')
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch(params) {
      this.searchForm = { ...params }
      this.page.current = 1
      this.getTableData()
    },

    // 导出Excel
    async handleExport(formParams) {
      try {
        await this.$confirm('确认导出当前数据吗？', '提示', {
          type: 'warning'
        })

        const params = {
          aggregations: formParams.aggregationType.join(','),
          partnerIds: formParams.partners.join(','),
          appids: formParams.channels.join(','),
          startMonth: formParams.monthRange[0],
          endMonth: formParams.monthRange[1],
        }

        console.log('导出参数', params);
                
        const { data } = await exportCostConfirmationList(params)
        
        if (data) {
          const blob = new Blob([data])
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = '成本确认汇总_' + moment().format('YYYY-MM-DD') + '.xlsx'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
          this.$message.success('导出成功')
        }
      } catch (error) {
        console.error('导出失败:', error)
        if (error !== 'cancel') {
          this.$message.error('导出失败')
        }
      }
    },

    // 分页大小改变
    handleSizeChange(size) {
      this.page.size = size
      this.getTableData()
    },

    // 当前页改变
    handleCurrentChange(current) {
      this.page.current = current
      this.getTableData()
    }
  }
}
</script>

<style lang="scss" scoped>
.cost-confirmation-summary {
  .table-container {
    margin-top: 16px;
  }

  .pagination-container {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
    padding: 16px;
    background-color: #fff;
    border-radius: 4px;
  }
}
</style>
